import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { batch } from "react-redux";

// MUI hook for styling function (stateless) components
import { makeStyles } from "@material-ui/core/styles";

// MUI Components
import Typography from "@material-ui/core/Typography";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import CircularProgress from "@material-ui/core/CircularProgress";

// Icons
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import AlertIcon from "@material-ui/icons/Warning";
import LockIcon from "@material-ui/icons/Lock";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";

// Custom Icons
import { TargetIcon, ThicknessIcon } from "../../constants/customIcons";

// Other
import { colors } from "../../constants/colors";
import { handleNullValues } from "../../constants/utilityFunctions";
import { customTheme } from "../../constants/customTheme";
// Actions
import { setSampleTrianglePosition, selectSample } from "../../actions";
const useStyles = makeStyles({
  root: {
    height: "48px",
    borderTopLeftRadius: "4px",
    borderBottomLeftRadius: "4px",
    paddingLeft: "10px",
    "&.Mui-selected": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    "&.MuiListItem-button:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.16)",
    },
  },
  right: {
    flex: "1 0 auto",
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
    justifyContent: "space-between",
    marginRight: "15px",
  },
  iconIncluded: customTheme().iconMedium,
  iconExcluded: {
    ...customTheme().iconMedium,
    opacity: 0.3,
  },
  iconPicture: {
    ...customTheme().iconMedium,
    color: (props) => colors[props.theme].tertiary,
  },
  spinner: {
    marginRight: "3px",
  },
  value: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginRight: "20px",
  },
  valuePicture: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginRight: "20px",
    color: (props) => colors[props.theme].tertiary,
  },
  units: {
    fontSize: "1.5rem",
  },
  strikeThrough: {
    width: "180px",
    position: "absolute",
    height: "3px",
    borderRadius: "3px",
    top: "15px",
    backgroundColor: "white",
    borderStyle: "none",
  },
});
function Sample(props) {
  const classes = useStyles(props);

  const handleSelect = (e, id) => {
    if (props.selectable && !props.selected) {
      const menuItem = e.currentTarget;
      const trianglePosition = menuItem.offsetTop + menuItem.clientHeight / 2;

      batch(() => {
        props.selectSample(id);
        props.setSampleTrianglePosition(trianglePosition);
      });
    }
  };

  // Sub Components
  const AcqTypeIcon = () => {
    switch (props.type) {
      case "BMODE":
        return (
          <ThicknessIcon
            className={
              props.includeInStudy ? classes.iconIncluded : classes.iconExcluded
            }
          />
        );
      case "PICTURE":
        return (
          <PhotoCameraIcon
            className={
              props.includeInStudy ? classes.iconIncluded : classes.iconExcluded
            }
          />
        );
      default:
        return (
          <TargetIcon
            className={
              props.includeInStudy ? classes.iconIncluded : classes.iconExcluded
            }
          />
        );
    }
  };

  const SampleStatusIcon = () => {
    if (props.includeInStudy) {
      if (!props.valueDisplay) {
        // Sample NOT DONE
        return (
          <CircularProgress
            size={30}
            color={props.type === "BMODE" ? "secondary" : "primary"}
            className={classes.spinner}
          />
        );
      } else {
        // Sample DONE
        if (props.locked) {
          return (
            <LockIcon
              color={props.type === "BMODE" ? "secondary" : "primary"}
              className={
                props.type === "PICTURE"
                  ? classes.iconPicture
                  : classes.iconIncluded
              }
            />
          );
        } else if (props.alert.state) {
          return <AlertIcon color="error" className={classes.iconIncluded} />;
        } else {
          return (
            <CheckBoxIcon
              color={props.type === "BMODE" ? "secondary" : "primary"}
              className={
                props.type === "PICTURE"
                  ? classes.iconPicture
                  : classes.iconIncluded
              }
            />
          );
        }
      }
    } else {
      if (props.alert.state) {
        return <AlertIcon color="error" className={classes.iconIncluded} />;
      } else {
        // No icon for samples not included in study
        return <div className={classes.iconIncluded} />;
      }
    }
  };

  const SampleValue = () => {
    let dynamicStyle = {
      opacity: props.includeInStudy ? "1" : "0.3",
    };

    if (props.type !== "PICTURE") {
      if (!props.valueDisplay) {
        return (
          <Typography
            style={dynamicStyle}
            color={props.type === "BMODE" ? "secondary" : "primary"}
            className={classes.value}
          ></Typography>
        );
      } else {
        return (
          <Typography
            style={dynamicStyle}
            className={classes.value}
            color={props.type === "BMODE" ? "secondary" : "primary"}
          >
            {handleSampleValues()}
            <span className={classes.units}>
              {props.type === "BMODE" ? " mm" : " m/s"}
            </span>
          </Typography>
        );
      }
    } else if (props.type === "PICTURE") {
      let locationPhotoSamples = [];
      for (let sampleId of props.locationSamples) {
        if (props.photoIds.includes(sampleId)) {
          locationPhotoSamples.push(sampleId);
        }
      }
      const sampleIndex = locationPhotoSamples.indexOf(props.id);
      return (
        <Typography
          style={dynamicStyle}
          className={classes.valuePicture}
          color={props.type === "BMODE" ? "secondary" : "primary"}
        >
          Photo {sampleIndex + 1}
        </Typography>
      );
    }
  };

  // Helper Functions
  const handleSampleValues = () => {
    // based on sample type and sample valueDisplay return the correct value to be displayed for the sample entry
    switch (props.type) {
      case "PICTURE":
        return handleNullValues(null);
      case "BMODE":
        return handleNullValues(props.thickness);
      default:
        return handleNullValues(props.shearWaveSpeed);
    }
  };

  return (
    <div>
      {!props.hide && ( // remove any samples that had an acquisition error from the visible SampleList
        <ListItem
          button
          selected={props.selected}
          className={classes.root}
          onClick={(e) => handleSelect(e, props.id)}
          style={{
            // underline if sample is in focus
            boxShadow: props.focused
              ? `inset 0px -2px 0px 0px ${(props) =>
                  colors[props.theme].primary}`
              : "0px 0px 0px 0px",
          }}
        >
          {/* STRIKE THROUGH IF SAMPLE NOT INCLUDED */}
          {!props.includeInStudy && <hr className={classes.strikeThrough} />}
          {/* SAMPLE INFO */}
          <ListItemIcon>
            <AcqTypeIcon />
          </ListItemIcon>
          <div className={classes.right}>
            <SampleStatusIcon />
            <SampleValue />
          </div>
        </ListItem>
      )}
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  const sample = state.samples.byId[ownProps.id];

  return {
    shearWaveSpeed: sample.shearWaveSpeed,
    thickness: sample.thickness,
    valueDisplay: sample.valueDisplay,
    alert: sample.alert,
    locked: sample.locked,
    type: sample.type,
    includeInStudy: sample.includeInStudy,
    focused: sample.id === state.utility.focusElement, // bool
    selected: sample.id === state.samples.selectedSampleId, // bool
    selectable: sample.selectable, // bool
    hide: sample.error.state && sample.error.type === "ACQ", // if the sample errored during acquisition, hide it
    photoIds: state.samples.idByType.PICTURE, // array of photo sample Ids
    locationSamples: state.locations.byId[sample.locationId].samples, // array of photo sample Ids
    theme: state.utility.theme,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      setSampleTrianglePosition,
      selectSample,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(Sample));
